import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Plus, Package, TrendingDown, DollarSign, Edit, Trash2, X, Save, Calculator, Beaker } from 'lucide-react';
import { batchService } from '../services/batchService';
import { farmProfileService } from '../services/farmProfileService';
import { designTokens } from '../design/designTokens';

// Feed Management Interfaces
interface FeedInventory {
  id: string;
  date: string;
  feedType: string;
  quantity: number;
  unit: 'kg' | 'bags' | 'tons';
  cost: number;
  currency: string;
  supplier: string;
  batchNumber: string;
  expiryDate?: string;
  notes?: string;
}

interface FeedConsumption {
  id: string;
  date: string;
  batchId: string;
  batchName: string;
  feedType: string;
  quantity: number;
  unit: 'kg' | 'bags' | 'tons';
  cost: number;
  currency: string;
  age: number;
  notes?: string;
}

const FEED_STORAGE_KEY = 'feed-management-data';

// Using global farm profile service

// Currency Configuration
interface Currency {
  code: string;
  name: string;
  symbol: string;
  region: string;
}

const CURRENCIES: Currency[] = [
  // Major International Currencies
  { code: 'USD', name: 'US Dollar', symbol: '$', region: 'International' },
  { code: 'EUR', name: 'Euro', symbol: '€', region: 'Europe' },
  { code: 'GBP', name: 'British Pound', symbol: '£', region: 'UK' },
  
  // East African Currencies
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', region: 'East Africa' },
  { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh', region: 'East Africa' },
  { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh', region: 'East Africa' },
  { code: 'RWF', name: 'Rwandan Franc', symbol: 'RF', region: 'East Africa' },
  { code: 'BIF', name: 'Burundian Franc', symbol: 'FBu', region: 'East Africa' },
  { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br', region: 'East Africa' },
  { code: 'SOS', name: 'Somali Shilling', symbol: 'S', region: 'East Africa' },
  { code: 'DJF', name: 'Djiboutian Franc', symbol: 'Fdj', region: 'East Africa' },
  
  // West African Currencies
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', region: 'West Africa' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵', region: 'West Africa' },
  { code: 'XOF', name: 'West African CFA Franc', symbol: 'CFA', region: 'West Africa' },
  { code: 'XAF', name: 'Central African CFA Franc', symbol: 'FCFA', region: 'Central Africa' },
  { code: 'SLL', name: 'Sierra Leonean Leone', symbol: 'Le', region: 'West Africa' },
  { code: 'LRD', name: 'Liberian Dollar', symbol: 'L$', region: 'West Africa' },
  { code: 'GMD', name: 'Gambian Dalasi', symbol: 'D', region: 'West Africa' },
  { code: 'GNF', name: 'Guinean Franc', symbol: 'FG', region: 'West Africa' },
  
  // Southern African Currencies
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', region: 'Southern Africa' },
  { code: 'BWP', name: 'Botswana Pula', symbol: 'P', region: 'Southern Africa' },
  { code: 'SZL', name: 'Swazi Lilangeni', symbol: 'L', region: 'Southern Africa' },
  { code: 'LSL', name: 'Lesotho Loti', symbol: 'L', region: 'Southern Africa' },
  { code: 'NAD', name: 'Namibian Dollar', symbol: 'N$', region: 'Southern Africa' },
  { code: 'ZMW', name: 'Zambian Kwacha', symbol: 'ZK', region: 'Southern Africa' },
  { code: 'ZWL', name: 'Zimbabwean Dollar', symbol: 'Z$', region: 'Southern Africa' },
  { code: 'MZN', name: 'Mozambican Metical', symbol: 'MT', region: 'Southern Africa' },
  { code: 'MGA', name: 'Malagasy Ariary', symbol: 'Ar', region: 'Southern Africa' },
  { code: 'MUR', name: 'Mauritian Rupee', symbol: '₨', region: 'Southern Africa' },
  { code: 'SCR', name: 'Seychellois Rupee', symbol: '₨', region: 'Southern Africa' },
  
  // North African Currencies
  { code: 'EGP', name: 'Egyptian Pound', symbol: '£', region: 'North Africa' },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'د.م.', region: 'North Africa' },
  { code: 'TND', name: 'Tunisian Dinar', symbol: 'د.ت', region: 'North Africa' },
  { code: 'DZD', name: 'Algerian Dinar', symbol: 'د.ج', region: 'North Africa' },
  { code: 'LYD', name: 'Libyan Dinar', symbol: 'ل.د', region: 'North Africa' },
  { code: 'SDG', name: 'Sudanese Pound', symbol: 'ج.س.', region: 'North Africa' },
  
  // Central African Currencies
  { code: 'CDF', name: 'Congolese Franc', symbol: 'FC', region: 'Central Africa' },
  { code: 'AOA', name: 'Angolan Kwanza', symbol: 'Kz', region: 'Central Africa' },
  { code: 'ZMK', name: 'Zambian Kwacha (Old)', symbol: 'ZK', region: 'Central Africa' },
  { code: 'MWK', name: 'Malawian Kwacha', symbol: 'MK', region: 'Central Africa' },
  
  // Other African Currencies
  { code: 'ERN', name: 'Eritrean Nakfa', symbol: 'Nfk', region: 'Horn of Africa' },
  { code: 'STN', name: 'São Tomé and Príncipe Dobra', symbol: 'Db', region: 'West Africa' },
  { code: 'CVE', name: 'Cape Verdean Escudo', symbol: '$', region: 'West Africa' },
  { code: 'KMF', name: 'Comorian Franc', symbol: 'CF', region: 'East Africa' },
];

// Currency utility functions
const getCurrencyByCode = (code: string): Currency | undefined => {
  return CURRENCIES.find(currency => currency.code === code);
};

const formatCurrency = (amount: number, currencyCode: string): string => {
  const currency = getCurrencyByCode(currencyCode);
  if (!currency) return `${amount.toLocaleString()}`;
  
  // Handle different currency symbol positions
  if (currency.symbol === '$' || currency.symbol === '€' || currency.symbol === '£') {
    return `${currency.symbol}${amount.toLocaleString()}`;
  } else {
    return `${amount.toLocaleString()} ${currency.symbol}`;
  }
};

// Feed Formulation Interfaces

interface FormulatedFeed {
  id: string;
  name: string;
  feedType: 'starter' | 'grower' | 'finisher' | 'layer';
  ingredients: Array<{
    ingredientId: string;
    ingredientName: string;
    percentage: number;
    cost: number;
  }>;
  totalCost: number;
  nutritionalProfile: {
    crudeProtein: number;
    crudeFat: number;
    crudeFiber: number;
    ash: number;
    calcium: number;
    phosphorus: number;
    lysine: number;
    methionine: number;
  };
  createdAt: string;
}

// Feed formulation types from Tools project
type BirdType = 'layers' | 'broilers' | 'sasso/kroilers' | 'local';
type FeedType = 'ingredients' | 'complete';

interface FeedItem {
  id: string;
  name: string;
  kgPerTon: number;
  pricePerKg: number;
}

// Base ingredients (common to all formulations)
const BASE_INGREDIENTS = {
  layers: [
    { name: "Maize", suggestedKgPerTon: 550, pricePerKg: 1200 },
    { name: "Soybean Meal", suggestedKgPerTon: 250, pricePerKg: 3200 },
    { name: "Sunflower Cake", suggestedKgPerTon: 100, pricePerKg: 2800 },
    { name: "Stock Feed Lime", suggestedKgPerTon: 20, pricePerKg: 800 },
  ],
  broilers: [
    { name: "Maize", suggestedKgPerTon: 580, pricePerKg: 1200 },
    { name: "Soybean Meal", suggestedKgPerTon: 280, pricePerKg: 3200 },
    { name: "Sunflower Cake", suggestedKgPerTon: 80, pricePerKg: 2800 },
    { name: "Stock Feed Lime", suggestedKgPerTon: 0, pricePerKg: 800 },
  ],
  "sasso/kroilers": [
    { name: "Maize", suggestedKgPerTon: 570, pricePerKg: 1200 },
    { name: "Soybean Meal", suggestedKgPerTon: 270, pricePerKg: 3200 },
    { name: "Sunflower Cake", suggestedKgPerTon: 90, pricePerKg: 2800 },
    { name: "Stock Feed Lime", suggestedKgPerTon: 0, pricePerKg: 800 },
  ],
  local: [
    { name: "Maize", suggestedKgPerTon: 550, pricePerKg: 1200 },
    { name: "Soybean Meal", suggestedKgPerTon: 250, pricePerKg: 3200 },
    { name: "Sunflower Cake", suggestedKgPerTon: 100, pricePerKg: 2800 },
    { name: "Stock Feed Lime", suggestedKgPerTon: 20, pricePerKg: 800 },
  ],
};

// Commercial concentrate options (mutually exclusive)
const CONCENTRATE_OPTIONS = {
  layers: [
    { name: "Hendrix Layer Concentrate (5%)", suggestedKgPerTon: 50, pricePerKg: 8500, brand: "Hendrix" },
    { name: "Hendrix Layer Concentrate (20%)", suggestedKgPerTon: 30, pricePerKg: 12500, brand: "Hendrix" },
    { name: "Koudijs Layer Concentrate (5%)", suggestedKgPerTon: 50, pricePerKg: 9000, brand: "Koudijs" },
    { name: "Koudijs Layer Concentrate (20%)", suggestedKgPerTon: 30, pricePerKg: 13000, brand: "Koudijs" },
    { name: "Lion Pro Layer Concentrate (5%)", suggestedKgPerTon: 50, pricePerKg: 8200, brand: "Lion Pro" },
    { name: "Lion Pro Layer Concentrate (20%)", suggestedKgPerTon: 30, pricePerKg: 12000, brand: "Lion Pro" },
  ],
  broilers: [
    { name: "Hendrix Broiler Concentrate (20%)", suggestedKgPerTon: 20, pricePerKg: 12500, brand: "Hendrix" },
    { name: "Hendrix Broiler Concentrate (25%)", suggestedKgPerTon: 15, pricePerKg: 13500, brand: "Hendrix" },
    { name: "Koudijs Broiler Concentrate (5%)", suggestedKgPerTon: 40, pricePerKg: 9000, brand: "Koudijs" },
    { name: "Koudijs Broiler Concentrate (20%)", suggestedKgPerTon: 20, pricePerKg: 13000, brand: "Koudijs" },
    { name: "Lion Pro Broiler Concentrate (5%)", suggestedKgPerTon: 40, pricePerKg: 8200, brand: "Lion Pro" },
    { name: "Lion Pro Broiler Concentrate (20%)", suggestedKgPerTon: 20, pricePerKg: 12000, brand: "Lion Pro" },
  ],
  "sasso/kroilers": [
    { name: "Hendrix Broiler Concentrate (20%)", suggestedKgPerTon: 25, pricePerKg: 12500, brand: "Hendrix" },
    { name: "Hendrix Broiler Concentrate (25%)", suggestedKgPerTon: 20, pricePerKg: 13500, brand: "Hendrix" },
    { name: "Koudijs Broiler Concentrate (5%)", suggestedKgPerTon: 45, pricePerKg: 9000, brand: "Koudijs" },
    { name: "Koudijs Broiler Concentrate (20%)", suggestedKgPerTon: 25, pricePerKg: 13000, brand: "Koudijs" },
    { name: "Lion Pro Broiler Concentrate (5%)", suggestedKgPerTon: 45, pricePerKg: 8200, brand: "Lion Pro" },
    { name: "Lion Pro Broiler Concentrate (20%)", suggestedKgPerTon: 25, pricePerKg: 12000, brand: "Lion Pro" },
  ],
  local: [
    { name: "Hendrix Layer Concentrate (5%)", suggestedKgPerTon: 50, pricePerKg: 8500, brand: "Hendrix" },
    { name: "Hendrix Layer Concentrate (20%)", suggestedKgPerTon: 30, pricePerKg: 12500, brand: "Hendrix" },
    { name: "Koudijs Layer Concentrate (5%)", suggestedKgPerTon: 50, pricePerKg: 9000, brand: "Koudijs" },
    { name: "Koudijs Layer Concentrate (20%)", suggestedKgPerTon: 30, pricePerKg: 13000, brand: "Koudijs" },
    { name: "Lion Pro Layer Concentrate (5%)", suggestedKgPerTon: 50, pricePerKg: 8200, brand: "Lion Pro" },
    { name: "Lion Pro Layer Concentrate (20%)", suggestedKgPerTon: 30, pricePerKg: 12000, brand: "Lion Pro" },
  ],
};

const Container = styled.div`
  padding: ${designTokens.spacing[6]};
  max-width: 1200px;
  margin: 0 auto;
  font-family: ${designTokens.typography.fontFamily.primary};
  background: ${designTokens.colors.background.primary};
  color: ${designTokens.colors.text.primary};
  min-height: 100vh;

  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[4]};
  }
`;


const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${designTokens.spacing[6]};
  margin-bottom: ${designTokens.spacing[8]};

  ${designTokens.mediaQueries.maxMd} {
    grid-template-columns: 1fr;
    gap: ${designTokens.mobileSpacing[4]};
  }
`;

const StatCard = styled.div`
  background: ${designTokens.colors.background.secondary};
  border-radius: ${designTokens.borderRadius.lg};
  padding: ${designTokens.spacing[6]};
  box-shadow: ${designTokens.shadows.card};
  border: 1px solid ${designTokens.colors.border.primary};
  transition: ${designTokens.transitions.default};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${designTokens.shadows.cardHover};
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing[3]};
  margin-bottom: ${designTokens.spacing[4]};
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${designTokens.borderRadius.md};
  background: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
`;

const StatTitle = styled.h3`
  font-size: ${designTokens.typography.fontSize.base};
  font-weight: ${designTokens.typography.fontWeight.semibold};
  color: ${designTokens.colors.text.primary};
  margin: 0;

  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.sm};
  }
`;

const StatValue = styled.div`
  font-size: ${designTokens.typography.fontSize['2xl']};
  font-weight: ${designTokens.typography.fontWeight.bold};
  color: ${designTokens.riverPoultry.brandColors.primary};
  margin-bottom: ${designTokens.spacing[2]};

  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.xl};
  }
`;

const StatSubtext = styled.div`
  font-size: ${designTokens.typography.fontSize.sm};
  color: ${designTokens.colors.text.muted};

  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.xs};
  }
`;

const ActionBar = styled.div`
  display: flex;
  gap: ${designTokens.spacing[4]};
  margin-bottom: ${designTokens.spacing[8]};
  flex-wrap: wrap;

  ${designTokens.mediaQueries.maxMd} {
    flex-direction: column;
    gap: ${designTokens.mobileSpacing[3]};
  }
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: ${designTokens.spacing[4]} ${designTokens.spacing[7]};
  border-radius: ${designTokens.borderRadius.full};
  border: none;
  font-size: ${designTokens.typography.fontSize.base};
  font-weight: ${designTokens.typography.fontWeight.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing[2]};
  transition: ${designTokens.transitions.default};
  font-family: ${designTokens.typography.fontFamily.primary};

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${designTokens.riverPoultry.gradients.primary};
          color: ${designTokens.colors.text.inverse};
          box-shadow: ${designTokens.shadows.button};
          &:hover { transform: translateY(-1px); box-shadow: ${designTokens.shadows.buttonHover}; }
        `;
      case 'secondary':
        return `
          background: ${designTokens.colors.background.tertiary};
          color: ${designTokens.colors.text.muted};
          border: 1px solid ${designTokens.colors.border.primary};
          &:hover { background: ${designTokens.colors.background.hover}; }
        `;
    }
  }}

  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[3]} ${designTokens.mobileSpacing[6]};
    font-size: ${designTokens.mobileTypography.fontSize.sm};
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e0e0e0;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
`;

const TableCell = styled.td`
  padding: 16px;
  color: #666;
  font-size: 0.9rem;
`;

const Badge = styled.span<{ color: string }>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => props.color}20;
  color: ${props => props.color};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' | 'danger' }>`
  padding: 8px 16px;
  border-radius: 20px;
  border: none;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #2e7d32;
          color: white;
          &:hover { background: #1b5e20; }
        `;
      case 'secondary':
        return `
          background: #f5f5f5;
          color: #666;
          &:hover { background: #e0e0e0; }
        `;
      case 'danger':
        return `
          background: #ffebee;
          color: #d32f2f;
          &:hover { background: #ffcdd2; }
        `;
    }
  }}
`;

// Modal and Form Components
const Modal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #2e7d32;
    box-shadow: 0 0 0 2px rgba(46, 125, 50, 0.1);
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #2e7d32;
    box-shadow: 0 0 0 2px rgba(46, 125, 50, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #2e7d32;
    box-shadow: 0 0 0 2px rgba(46, 125, 50, 0.1);
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const FeedManagement: React.FC = () => {
  // State management
  const [feedInventory, setFeedInventory] = useState<FeedInventory[]>([]);
  const [feedConsumption, setFeedConsumption] = useState<FeedConsumption[]>([]);
  const [batches, setBatches] = useState<any[]>([]);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showConsumptionModal, setShowConsumptionModal] = useState(false);
  const [showFormulatorModal, setShowFormulatorModal] = useState(false);
  const [editingInventoryId, setEditingInventoryId] = useState<string | null>(null);
  const [editingConsumptionId, setEditingConsumptionId] = useState<string | null>(null);
  const [inventoryForm, setInventoryForm] = useState({
    date: new Date().toISOString().split('T')[0],
    feedType: '',
    quantity: '',
    unit: 'kg' as 'kg' | 'bags' | 'tons',
    cost: '',
    currency: 'UGX',
    supplier: '',
    batchNumber: '',
    expiryDate: '',
    notes: ''
  });
  const [consumptionForm, setConsumptionForm] = useState({
    date: new Date().toISOString().split('T')[0],
    batchId: '',
    feedType: '',
    quantity: '',
    unit: 'kg' as 'kg' | 'bags' | 'tons',
    notes: ''
  });
  
  // Feed Formulation State
  const [formulatedFeeds, setFormulatedFeeds] = useState<FormulatedFeed[]>([]);
  const [formulationForm, setFormulationForm] = useState({
    name: '',
    birdType: 'broilers' as BirdType,
    feedType: 'ingredients' as FeedType,
    selectedConcentrate: '',
    feedItems: [] as FeedItem[]
  });

  const loadFeedData = () => {
    try {
      const saved = localStorage.getItem(FEED_STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        setFeedInventory(data.inventory || []);
        setFeedConsumption(data.consumption || []);
      } else {
        // Load default sample data
        const defaultData = {
          inventory: [
            {
              id: '1',
              date: '2024-01-20',
              feedType: 'Starter Feed',
              quantity: 50,
              unit: 'kg' as 'kg' | 'bags' | 'tons',
              cost: 2500,
              currency: 'UGX',
              supplier: 'FeedCo Ltd',
              batchNumber: 'ST-2024-001',
              expiryDate: '2024-12-31',
              notes: 'High quality starter feed'
            },
            {
              id: '2',
              date: '2024-01-18',
              feedType: 'Grower Feed',
              quantity: 100,
              unit: 'kg' as 'kg' | 'bags' | 'tons',
              cost: 4500,
              currency: 'UGX',
              supplier: 'FeedCo Ltd',
              batchNumber: 'GR-2024-002',
              expiryDate: '2024-12-31',
              notes: 'Premium grower feed'
            }
          ],
          consumption: [
            {
              id: '1',
              date: '2024-01-20',
              batchId: '1',
              batchName: 'Batch A-2024-01',
              feedType: 'Starter Feed',
              quantity: 25,
              unit: 'kg' as 'kg' | 'bags' | 'tons',
              cost: 1250,
              currency: 'UGX',
              age: 21,
              notes: 'Morning feeding'
            }
          ]
        };
        setFeedInventory(defaultData.inventory);
        setFeedConsumption(defaultData.consumption);
        localStorage.setItem(FEED_STORAGE_KEY, JSON.stringify(defaultData));
      }
    } catch (error) {
      console.error('Error loading feed data:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadFeedData();
    loadBatches();
    setFormulatedFeeds(loadFormulatedFeeds());
  }, []);

  // Enhanced data validation
  const validateInventoryForm = (): string[] => {
    const errors: string[] = [];
    
    if (!inventoryForm.date) errors.push('Date is required');
    if (!inventoryForm.feedType.trim()) errors.push('Feed type is required');
    if (!inventoryForm.quantity || parseFloat(inventoryForm.quantity) <= 0) {
      errors.push('Valid quantity is required');
    }
    if (!inventoryForm.cost || parseFloat(inventoryForm.cost) <= 0) {
      errors.push('Valid cost is required');
    }
    if (!inventoryForm.supplier.trim()) errors.push('Supplier is required');
    if (!inventoryForm.batchNumber.trim()) errors.push('Batch number is required');
    
    return errors;
  };

  const validateConsumptionForm = (): string[] => {
    const errors: string[] = [];
    
    if (!consumptionForm.date) errors.push('Date is required');
    if (!consumptionForm.batchId) errors.push('Batch selection is required');
    if (!consumptionForm.feedType.trim()) errors.push('Feed type is required');
    if (!consumptionForm.quantity || parseFloat(consumptionForm.quantity) <= 0) {
      errors.push('Valid quantity is required');
    }
    
    return errors;
  };


  const loadBatches = () => {
    const loadedBatches = batchService.loadBatches();
    setBatches(loadedBatches);
  };

  const saveFeedData = (data: any) => {
    try {
      localStorage.setItem(FEED_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving feed data:', error);
    }
  };

  // Calculate metrics
  const totalInventory = feedInventory.reduce((sum, item) => sum + item.quantity, 0);
  const totalConsumption = feedConsumption.reduce((sum, item) => sum + item.quantity, 0);
  const totalValue = feedInventory.reduce((sum, item) => sum + item.cost, 0);
  const avgCostPerKg = totalInventory > 0 ? totalValue / totalInventory : 0;

  // Form handlers
  const handleInventorySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    const errors = validateInventoryForm();
    if (errors.length > 0) {
      // Validation errors will be shown inline
      return;
    }
    
    if (editingInventoryId) {
      // Update existing inventory
      const updatedInventory = feedInventory.map(item => 
        item.id === editingInventoryId 
          ? {
              ...item,
              ...inventoryForm,
              quantity: parseFloat(inventoryForm.quantity),
              cost: parseFloat(inventoryForm.cost)
            }
          : item
      );
      setFeedInventory(updatedInventory);
      saveFeedData({ inventory: updatedInventory, consumption: feedConsumption });
      setEditingInventoryId(null);
      // Feed inventory updated successfully
    } else {
      // Add new inventory
      const newInventory: FeedInventory = {
        id: Date.now().toString(),
        ...inventoryForm,
        quantity: parseFloat(inventoryForm.quantity),
        cost: parseFloat(inventoryForm.cost)
      };
      
      const updatedInventory = [...feedInventory, newInventory];
      setFeedInventory(updatedInventory);
      saveFeedData({ inventory: updatedInventory, consumption: feedConsumption });
      // Feed inventory added successfully
    }
    
    setShowInventoryModal(false);
    resetInventoryForm();
  };

  const handleConsumptionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    const errors = validateConsumptionForm();
    if (errors.length > 0) {
      // Validation errors will be shown inline
      return;
    }
    
    const selectedBatch = batches.find(b => b.id === consumptionForm.batchId);
    
    if (editingConsumptionId) {
      // Update existing consumption
      const updatedConsumption = feedConsumption.map(item => 
        item.id === editingConsumptionId 
          ? {
              ...item,
              ...consumptionForm,
              batchName: selectedBatch?.name || 'Unknown Batch',
              quantity: parseFloat(consumptionForm.quantity),
              age: selectedBatch?.age || 0
            }
          : item
      );
      setFeedConsumption(updatedConsumption);
      saveFeedData({ inventory: feedInventory, consumption: updatedConsumption });
      setEditingConsumptionId(null);
      // Feed consumption updated successfully
    } else {
      // Add new consumption
      const newConsumption: FeedConsumption = {
        id: Date.now().toString(),
        ...consumptionForm,
        batchName: selectedBatch?.name || 'Unknown Batch',
        quantity: parseFloat(consumptionForm.quantity),
        cost: 0, // No cost tracking for consumption
        currency: 'UGX', // Default currency
        age: selectedBatch?.age || 0
      };
      
      const updatedConsumption = [...feedConsumption, newConsumption];
      setFeedConsumption(updatedConsumption);
      saveFeedData({ inventory: feedInventory, consumption: updatedConsumption });
      // Feed consumption recorded successfully
    }
    
    setShowConsumptionModal(false);
    resetConsumptionForm();
  };

  const resetInventoryForm = () => {
    setInventoryForm({
      date: new Date().toISOString().split('T')[0],
      feedType: '',
      quantity: '',
      unit: 'kg',
      cost: '',
      currency: 'UGX',
      supplier: '',
      batchNumber: '',
      expiryDate: '',
      notes: ''
    });
  };

  const resetConsumptionForm = () => {
    setConsumptionForm({
      date: new Date().toISOString().split('T')[0],
      batchId: '',
      feedType: '',
      quantity: '',
      unit: 'kg',
      notes: ''
    });
  };

  // Edit and Delete Functions
  const handleEditInventory = (id: string) => {
    const item = feedInventory.find(i => i.id === id);
    if (item) {
      setInventoryForm({
        date: item.date,
        feedType: item.feedType,
        quantity: item.quantity.toString(),
        unit: item.unit,
        cost: item.cost.toString(),
        currency: item.currency,
        supplier: item.supplier,
        batchNumber: item.batchNumber,
        expiryDate: item.expiryDate || '',
        notes: item.notes || ''
      });
      setEditingInventoryId(id);
      setShowInventoryModal(true);
    }
  };

  const handleDeleteInventory = (id: string) => {
    if (window.confirm('Are you sure you want to delete this inventory item?')) {
      const updatedInventory = feedInventory.filter(item => item.id !== id);
      setFeedInventory(updatedInventory);
      saveFeedData({ inventory: updatedInventory, consumption: feedConsumption });
    }
  };

  const handleEditConsumption = (id: string) => {
    const item = feedConsumption.find(i => i.id === id);
    if (item) {
      setConsumptionForm({
        date: item.date,
        batchId: item.batchId,
        feedType: item.feedType,
        quantity: item.quantity.toString(),
        unit: item.unit,
        notes: item.notes || ''
      });
      setEditingConsumptionId(id);
      setShowConsumptionModal(true);
    }
  };

  const handleDeleteConsumption = (id: string) => {
    if (window.confirm('Are you sure you want to delete this consumption record?')) {
      const updatedConsumption = feedConsumption.filter(item => item.id !== id);
      setFeedConsumption(updatedConsumption);
      saveFeedData({ inventory: feedInventory, consumption: updatedConsumption });
    }
  };

  // Feed Formulation Functions (from Tools project)
  const calculateFeedCostPerKg = (feedItems: FeedItem[]) => {
    return feedItems.reduce((sum, f) => {
      const proportion = (f.kgPerTon || 0) / 1000;
      return sum + proportion * (f.pricePerKg || 0);
    }, 0);
  };

  const addFeedItem = () => {
    const newItem: FeedItem = {
      id: Date.now().toString(),
      name: '',
      kgPerTon: 0,
      pricePerKg: 0
    };
    setFormulationForm(prev => ({
      ...prev,
      feedItems: [...prev.feedItems, newItem]
    }));
  };

  const removeFeedItem = (id: string) => {
    setFormulationForm(prev => ({
      ...prev,
      feedItems: prev.feedItems.filter(item => item.id !== id)
    }));
  };

  const updateFeedItem = (id: string, field: keyof FeedItem, value: string | number) => {
    setFormulationForm(prev => ({
      ...prev,
      feedItems: prev.feedItems.map(item => 
        item.id === id ? {...item, [field]: value} : item
      )
    }));
  };

  const applySuggestedIngredients = () => {
    const baseIngredients = BASE_INGREDIENTS[formulationForm.birdType];
    const concentrateOptions = CONCENTRATE_OPTIONS[formulationForm.birdType];
    
    // Start with base ingredients
    const newFeedItems = baseIngredients.map((ingredient, index) => ({
      id: String(Date.now() + index),
      name: ingredient.name,
      kgPerTon: ingredient.suggestedKgPerTon,
      pricePerKg: ingredient.pricePerKg
    }));
    
    // Add selected concentrate if one is chosen
    if (formulationForm.selectedConcentrate) {
      const selectedConcentrate = concentrateOptions.find(opt => opt.name === formulationForm.selectedConcentrate);
      if (selectedConcentrate) {
        newFeedItems.push({
          id: String(Date.now() + baseIngredients.length),
          name: selectedConcentrate.name,
          kgPerTon: selectedConcentrate.suggestedKgPerTon,
          pricePerKg: selectedConcentrate.pricePerKg
        });
      }
    }
    
    setFormulationForm(prev => ({
      ...prev,
      feedItems: newFeedItems
    }));
  };

  const handleFormulationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formulationForm.name.trim() === '') {
      // Feed name required
      return;
    }

    if (formulationForm.feedItems.length === 0) {
      // At least one ingredient required
      return;
    }

    const totalKgPerTon = formulationForm.feedItems.reduce((sum, item) => sum + item.kgPerTon, 0);
    if (totalKgPerTon === 0) {
      // Ingredient quantities required
      return;
    }

    const totalCost = calculateFeedCostPerKg(formulationForm.feedItems);

    const newFormulatedFeed: FormulatedFeed = {
      id: Date.now().toString(),
      name: formulationForm.name,
      feedType: formulationForm.feedType === 'ingredients' ? 'starter' : 'starter', // Map to existing type
      ingredients: formulationForm.feedItems.map(item => ({
        ingredientId: item.id,
        ingredientName: item.name,
        percentage: (item.kgPerTon / totalKgPerTon) * 100,
        cost: item.pricePerKg * item.kgPerTon / 1000
      })),
      totalCost,
      nutritionalProfile: {
        crudeProtein: 0, // Would need nutritional data for each ingredient
        crudeFat: 0,
        crudeFiber: 0,
        ash: 0,
        calcium: 0,
        phosphorus: 0,
        lysine: 0,
        methionine: 0
      },
      createdAt: new Date().toISOString()
    };

    setFormulatedFeeds(prev => [...prev, newFormulatedFeed]);
    saveFormulatedFeeds([...formulatedFeeds, newFormulatedFeed]);
    resetFormulationForm();
    setShowFormulatorModal(false);
  };

  const resetFormulationForm = () => {
    setFormulationForm({
      name: '',
      birdType: 'broilers',
      feedType: 'ingredients',
      selectedConcentrate: '',
      feedItems: []
    });
  };

  const saveFormulatedFeeds = (feeds: FormulatedFeed[]) => {
    try {
      localStorage.setItem('formulatedFeeds', JSON.stringify(feeds));
    } catch (error) {
      console.error('Error saving formulated feeds:', error);
    }
  };

  const loadFormulatedFeeds = (): FormulatedFeed[] => {
    try {
      const saved = localStorage.getItem('formulatedFeeds');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading formulated feeds:', error);
      return [];
    }
  };

  return (
    <Container>

      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatIcon color="#2E7D32">
              <Package size={24} />
            </StatIcon>
            <StatTitle>Total Inventory</StatTitle>
          </StatHeader>
          <StatValue>{totalInventory.toLocaleString()} kg</StatValue>
          <StatSubtext>Available stock</StatSubtext>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#FF8C00">
              <TrendingDown size={24} />
            </StatIcon>
            <StatTitle>Total Consumption</StatTitle>
          </StatHeader>
          <StatValue>{totalConsumption.toLocaleString()} kg</StatValue>
          <StatSubtext>This week</StatSubtext>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#E53E3E">
              <DollarSign size={24} />
            </StatIcon>
            <StatTitle>Inventory Value</StatTitle>
          </StatHeader>
          <StatValue>{formatCurrency(totalValue, 'UGX')}</StatValue>
          <StatSubtext>Total value</StatSubtext>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#9c27b0">
              <Package size={24} />
            </StatIcon>
            <StatTitle>Avg Cost/kg</StatTitle>
          </StatHeader>
          <StatValue>{formatCurrency(avgCostPerKg, 'UGX')}</StatValue>
          <StatSubtext>Average price</StatSubtext>
        </StatCard>
      </StatsGrid>

      <ActionBar>
        <Button variant="primary" onClick={() => setShowInventoryModal(true)}>
          <Plus size={20} />
          Add Feed Inventory
        </Button>
        <Button variant="secondary" onClick={() => setShowConsumptionModal(true)}>
          <Plus size={20} />
          Record Consumption
        </Button>
        <Button variant="secondary" onClick={() => setShowFormulatorModal(true)}>
          <Calculator size={20} />
          Feed Formulator
        </Button>
      </ActionBar>

      {/* Feed Inventory Table */}
      <TableContainer>
        <h3 style={{ margin: '0 0 20px 0', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Package size={20} />
          Feed Inventory
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Date</TableHeaderCell>
              <TableHeaderCell>Feed Type</TableHeaderCell>
              <TableHeaderCell>Quantity</TableHeaderCell>
              <TableHeaderCell>Cost</TableHeaderCell>
              <TableHeaderCell>Supplier</TableHeaderCell>
              <TableHeaderCell>Batch Number</TableHeaderCell>
              <TableHeaderCell>Expiry Date</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <tbody>
            {feedInventory.map((record) => (
              <TableRow key={record.id}>
                <TableCell>{record.date}</TableCell>
                <TableCell>
                  <Badge color="#2e7d32">{record.feedType}</Badge>
                </TableCell>
                <TableCell>{record.quantity} {record.unit}</TableCell>
                <TableCell>{formatCurrency(record.cost, record.currency)}</TableCell>
                <TableCell>{record.supplier}</TableCell>
                <TableCell>{record.batchNumber}</TableCell>
                <TableCell>{record.expiryDate || 'N/A'}</TableCell>
                <TableCell>
                  <ActionButtons>
                    <ActionButton variant="secondary" onClick={() => handleEditInventory(record.id)}>
                      <Edit size={14} />
                      Edit
                    </ActionButton>
                    <ActionButton variant="danger" onClick={() => handleDeleteInventory(record.id)}>
                      <Trash2 size={14} />
                      Delete
                    </ActionButton>
                  </ActionButtons>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      {/* Feed Consumption Table */}
      <TableContainer style={{ marginTop: '20px' }}>
        <h3 style={{ margin: '0 0 20px 0', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TrendingDown size={20} />
          Feed Consumption
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Date</TableHeaderCell>
              <TableHeaderCell>Batch</TableHeaderCell>
              <TableHeaderCell>Feed Type</TableHeaderCell>
              <TableHeaderCell>Quantity</TableHeaderCell>
              <TableHeaderCell>Age (days)</TableHeaderCell>
              <TableHeaderCell>Notes</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <tbody>
            {feedConsumption.map((record) => (
              <TableRow key={record.id}>
                <TableCell>{record.date}</TableCell>
                <TableCell>
                  <Badge color="#2196f3">{record.batchName}</Badge>
                </TableCell>
                <TableCell>
                  <Badge color="#ff9800">{record.feedType}</Badge>
                </TableCell>
                <TableCell>{record.quantity} {record.unit}</TableCell>
                <TableCell>{record.age}</TableCell>
                <TableCell>{record.notes || 'N/A'}</TableCell>
                <TableCell>
                  <ActionButtons>
                    <ActionButton variant="secondary" onClick={() => handleEditConsumption(record.id)}>
                      <Edit size={14} />
                      Edit
                    </ActionButton>
                    <ActionButton variant="danger" onClick={() => handleDeleteConsumption(record.id)}>
                      <Trash2 size={14} />
                      Delete
                    </ActionButton>
                  </ActionButtons>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      {/* Add Feed Inventory Modal */}
      <Modal isOpen={showInventoryModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{editingInventoryId ? 'Edit Feed Inventory' : 'Add Feed Inventory'}</ModalTitle>
            <CloseButton onClick={() => {
              setShowInventoryModal(false);
              setEditingInventoryId(null);
              resetInventoryForm();
            }}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>
          <Form onSubmit={handleInventorySubmit}>
            <FormGroup>
              <Label>Date</Label>
              <Input
                type="date"
                value={inventoryForm.date}
                onChange={(e) => setInventoryForm({...inventoryForm, date: e.target.value})}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Feed Type</Label>
              <Select
                value={inventoryForm.feedType}
                onChange={(e) => setInventoryForm({...inventoryForm, feedType: e.target.value})}
                required
              >
                <option value="">Select feed type</option>
                {farmProfileService.getFeedTypesForFarmType().map((feedType: string) => (
                  <option key={feedType} value={feedType}>{feedType}</option>
                ))}
              </Select>
            </FormGroup>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <FormGroup>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={inventoryForm.quantity}
                  onChange={(e) => setInventoryForm({...inventoryForm, quantity: e.target.value})}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Unit</Label>
                <Select
                  value={inventoryForm.unit}
                  onChange={(e) => setInventoryForm({...inventoryForm, unit: e.target.value as 'kg' | 'bags' | 'tons'})}
                >
                  <option value="kg">kg</option>
                  <option value="bags">bags</option>
                  <option value="tons">tons</option>
                </Select>
              </FormGroup>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
              <FormGroup>
                <Label>Cost</Label>
                <Input
                  type="number"
                  value={inventoryForm.cost}
                  onChange={(e) => setInventoryForm({...inventoryForm, cost: e.target.value})}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Currency</Label>
                <Select
                  value={inventoryForm.currency}
                  onChange={(e) => setInventoryForm({...inventoryForm, currency: e.target.value})}
                  required
                >
                  {CURRENCIES.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </div>
            <FormGroup>
              <Label>Supplier</Label>
              <Input
                type="text"
                value={inventoryForm.supplier}
                onChange={(e) => setInventoryForm({...inventoryForm, supplier: e.target.value})}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Batch Number</Label>
              <Input
                type="text"
                value={inventoryForm.batchNumber}
                onChange={(e) => setInventoryForm({...inventoryForm, batchNumber: e.target.value})}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Expiry Date</Label>
              <Input
                type="date"
                value={inventoryForm.expiryDate}
                onChange={(e) => setInventoryForm({...inventoryForm, expiryDate: e.target.value})}
              />
            </FormGroup>
            <FormGroup>
              <Label>Notes</Label>
              <TextArea
                value={inventoryForm.notes}
                onChange={(e) => setInventoryForm({...inventoryForm, notes: e.target.value})}
                placeholder="Additional notes about this feed..."
              />
            </FormGroup>
            <FormActions>
              <Button type="button" variant="secondary" onClick={() => {
                setShowInventoryModal(false);
                setEditingInventoryId(null);
                resetInventoryForm();
              }}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                <Save size={16} />
                {editingInventoryId ? 'Update Inventory' : 'Add Inventory'}
              </Button>
            </FormActions>
          </Form>
        </ModalContent>
      </Modal>

      {/* Record Feed Consumption Modal */}
      <Modal isOpen={showConsumptionModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{editingConsumptionId ? 'Edit Feed Consumption' : 'Record Feed Consumption'}</ModalTitle>
            <CloseButton onClick={() => {
              setShowConsumptionModal(false);
              setEditingConsumptionId(null);
              resetConsumptionForm();
            }}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>
          <Form onSubmit={handleConsumptionSubmit}>
            <FormGroup>
              <Label>Date</Label>
              <Input
                type="date"
                value={consumptionForm.date}
                onChange={(e) => setConsumptionForm({...consumptionForm, date: e.target.value})}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Batch</Label>
              <Select
                value={consumptionForm.batchId}
                onChange={(e) => setConsumptionForm({...consumptionForm, batchId: e.target.value})}
                required
              >
                <option value="">Select batch</option>
                {batches.filter(b => b.status === 'active').map(batch => (
                  <option key={batch.id} value={batch.id}>
                    {batch.name} (Age: {batch.age} days)
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Feed Type</Label>
              <Select
                value={consumptionForm.feedType}
                onChange={(e) => setConsumptionForm({...consumptionForm, feedType: e.target.value})}
                required
              >
                <option value="">Select feed type</option>
                {farmProfileService.getFeedTypesForFarmType().map((feedType: string) => (
                  <option key={feedType} value={feedType}>{feedType}</option>
                ))}
              </Select>
            </FormGroup>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <FormGroup>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={consumptionForm.quantity}
                  onChange={(e) => setConsumptionForm({...consumptionForm, quantity: e.target.value})}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Unit</Label>
                <Select
                  value={consumptionForm.unit}
                  onChange={(e) => setConsumptionForm({...consumptionForm, unit: e.target.value as 'kg' | 'bags' | 'tons'})}
                >
                  <option value="kg">kg</option>
                  <option value="bags">bags</option>
                  <option value="tons">tons</option>
                </Select>
              </FormGroup>
            </div>
            <FormGroup>
              <Label>Notes</Label>
              <TextArea
                value={consumptionForm.notes}
                onChange={(e) => setConsumptionForm({...consumptionForm, notes: e.target.value})}
                placeholder="Optional notes about this consumption..."
              />
            </FormGroup>
            <FormActions>
              <Button type="button" variant="secondary" onClick={() => {
                setShowConsumptionModal(false);
                setEditingConsumptionId(null);
                resetConsumptionForm();
              }}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                <Save size={16} />
                {editingConsumptionId ? 'Update Consumption' : 'Record Consumption'}
              </Button>
            </FormActions>
          </Form>
        </ModalContent>
      </Modal>

      {/* Feed Formulator Modal */}
      <Modal isOpen={showFormulatorModal}>
          <ModalContent style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
            <ModalHeader>
              <ModalTitle>
                <Beaker size={20} />
                Feed Formulator
              </ModalTitle>
              <CloseButton onClick={() => setShowFormulatorModal(false)}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>
            <Form onSubmit={handleFormulationSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '16px' }}>
                <FormGroup>
                  <Label>Feed Name</Label>
                  <Input
                    type="text"
                    value={formulationForm.name}
                    onChange={(e) => setFormulationForm({...formulationForm, name: e.target.value})}
                    placeholder="e.g., Broiler Starter Feed"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Bird Type</Label>
                  <Select
                    value={formulationForm.birdType}
                    onChange={(e) => setFormulationForm({...formulationForm, birdType: e.target.value as BirdType, selectedConcentrate: ''})}
                    required
                  >
                    <option value="broilers">Broilers</option>
                    <option value="layers">Layers</option>
                    <option value="sasso/kroilers">Sasso/Kroilers</option>
                    <option value="local">Local</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Concentrate Brand</Label>
                  <Select
                    value={formulationForm.selectedConcentrate}
                    onChange={(e) => setFormulationForm({...formulationForm, selectedConcentrate: e.target.value})}
                  >
                    <option value="">Select concentrate...</option>
                    {CONCENTRATE_OPTIONS[formulationForm.birdType].map(concentrate => (
                      <option key={concentrate.name} value={concentrate.name}>
                        {concentrate.brand} - {concentrate.name}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Feed Type</Label>
                  <Select
                    value={formulationForm.feedType}
                    onChange={(e) => setFormulationForm({...formulationForm, feedType: e.target.value as FeedType})}
                    required
                  >
                    <option value="ingredients">Own Formulated</option>
                    <option value="complete">Complete Feed</option>
                  </Select>
                </FormGroup>
              </div>

              <div style={{ margin: '20px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <Label>Feed Ingredients</Label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button type="button" variant="secondary" onClick={applySuggestedIngredients}>
                      <Package size={16} />
                      Apply Base + {formulationForm.selectedConcentrate ? 'Selected Concentrate' : 'No Concentrate'}
                    </Button>
                    <Button type="button" variant="secondary" onClick={addFeedItem}>
                      <Plus size={16} />
                      Add Custom Ingredient
                    </Button>
                  </div>
                </div>

                {formulationForm.feedItems.map((item) => (
                  <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '12px', alignItems: 'end', marginBottom: '12px' }}>
                    <FormGroup>
                      <Label>Ingredient Name</Label>
                      <Input
                        type="text"
                        value={item.name}
                        onChange={(e) => updateFeedItem(item.id, 'name', e.target.value)}
                        placeholder="e.g., Maize, Soybean Meal"
                        required
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Kg per Ton</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="1000"
                        value={item.kgPerTon}
                        onChange={(e) => updateFeedItem(item.id, 'kgPerTon', parseFloat(e.target.value) || 0)}
                        required
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Price per kg (UGX)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.pricePerKg}
                        onChange={(e) => updateFeedItem(item.id, 'pricePerKg', parseFloat(e.target.value) || 0)}
                        required
                      />
                    </FormGroup>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => removeFeedItem(item.id)}
                      style={{ padding: '8px', minWidth: 'auto' }}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}

                {formulationForm.feedItems.length > 0 && (
                  <div style={{ 
                    padding: '12px', 
                    backgroundColor: '#f5f5f5', 
                    borderRadius: '8px', 
                    marginTop: '16px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    color: '#2e7d32'
                  }}>
                    Total kg per ton: {formulationForm.feedItems.reduce((sum, item) => sum + item.kgPerTon, 0).toFixed(1)} kg
                  </div>
                )}
              </div>

              {formulationForm.feedItems.length > 0 && (
                <div style={{ margin: '20px 0', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>Feed Cost Summary</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', fontSize: '14px' }}>
                    <div><strong>Cost per kg of feed:</strong> {formatCurrency(calculateFeedCostPerKg(formulationForm.feedItems), 'UGX')}</div>
                    <div><strong>Total ingredients:</strong> {formulationForm.feedItems.length} items</div>
                    <div><strong>Total kg per ton:</strong> {formulationForm.feedItems.reduce((sum, item) => sum + item.kgPerTon, 0).toFixed(1)} kg</div>
                    <div><strong>Bird type:</strong> {formulationForm.birdType.charAt(0).toUpperCase() + formulationForm.birdType.slice(1)}</div>
                  </div>
                  <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#e8f5e8', borderRadius: '4px', fontSize: '12px', color: '#2e7d32' }}>
                    <strong>Note:</strong> {formulationForm.selectedConcentrate ? 
                      `This formulation uses ${formulationForm.selectedConcentrate} as the commercial concentrate. Only one concentrate brand should be used per formulation.` :
                      'This formulation uses base ingredients only. Select a concentrate brand above for commercial concentrate inclusion.'
                    }
                  </div>
                </div>
              )}

              <FormActions>
                <Button type="button" variant="secondary" onClick={() => setShowFormulatorModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  <Save size={16} />
                  Create Formulation
                </Button>
              </FormActions>
            </Form>
          </ModalContent>
        </Modal>

      {/* Formulated Feeds Table */}
      {formulatedFeeds.length > 0 && (
        <TableContainer style={{ marginTop: '40px' }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Calculator size={20} />
            Formulated Feeds
          </h3>
          <Table>
            <thead>
              <tr>
                <th>Feed Name</th>
                <th>Type</th>
                <th>Ingredients</th>
                <th>Protein (%)</th>
                <th>Cost/kg</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {formulatedFeeds.map((feed) => (
                <tr key={feed.id}>
                  <td>
                    <strong>{feed.name}</strong>
                  </td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: feed.feedType === 'starter' ? '#e3f2fd' : 
                                     feed.feedType === 'grower' ? '#e8f5e8' :
                                     feed.feedType === 'finisher' ? '#fff3e0' : '#fce4ec',
                      color: feed.feedType === 'starter' ? '#1976d2' :
                             feed.feedType === 'grower' ? '#388e3c' :
                             feed.feedType === 'finisher' ? '#f57c00' : '#c2185b'
                    }}>
                      {feed.feedType.charAt(0).toUpperCase() + feed.feedType.slice(1)}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                      {feed.ingredients.slice(0, 3).map(ing => (
                        <div key={ing.ingredientId}>
                          {ing.ingredientName}: {ing.percentage}%
                        </div>
                      ))}
                      {feed.ingredients.length > 3 && (
                        <div style={{ color: '#666' }}>+{feed.ingredients.length - 3} more...</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <strong>{feed.nutritionalProfile.crudeProtein.toFixed(1)}%</strong>
                  </td>
                  <td>
                    <strong>{formatCurrency(feed.totalCost, 'UGX')}</strong>
                  </td>
                  <td>
                    {new Date(feed.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}

    </Container>
  );
};

export default FeedManagement;